import React, { useState, useMemo, useCallback } from 'react';
import { sessionStatusOf } from '../utils/sessionMarks';
import {
  KeyIcon,
  InformationCircleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import CopyButton from './CopyButton';
import Pagination from './Pagination';
import { parseBrowser, formatTimestampShort } from './TimelineView';
import { useResizableCols } from './coverage/coverageShared';
import { getIPStatusTextClass, sessionStatusTone } from '../utils/riskColors';
import StatusCircle from './ui/StatusCircle';

/**
 * AAD Session IDs tab — one row per session, aggregating every event that
 * carries a session id.
 *
 * Sign-in `SessionId` and Exchange/SharePoint `AADSessionId` are ONE namespace
 * here, matching how CaseDetail already treats them for compromised-session
 * detection: Microsoft typically issues the same GUID for both layers of the
 * same logical session, so splitting them would show the analyst two half-rows
 * for one session.
 *
 * The point of the matrix is the IP column. A session is the strongest single
 * link between an attacker IP and the user's own activity — if one session id
 * spans a residential IP and a hosting/VPN IP, that is token replay or a shared
 * session, and it is very hard to see in a time-ordered timeline.
 */

// Workloads that can carry a session id. Same list CaseDetail walks for
// `compromisedSessions` / `otherSessionsSummary` — keep them in step.
const SESSION_SOURCES = [
  ['SignInEvents', 'Sign-In'],
  ['ExchangeEvents', 'Exchange'],
  ['SharePointEvents', 'SharePoint'],
  ['MessageTraceEvents', 'Message Trace'],
  ['UrlClickEvents', 'URL Click'],
  ['GeneralEvents', 'General'],
  ['AzureActivityEvents', 'Azure Activity'],
  ['RiskDetectionEvents', 'Risk Detection'],
  ['SecurityAlertEvents', 'Security Alert'],
];

// Status leads, matching IPAddressTable where the classification control is the
// first real column. Every classification affordance in this portal sits on the
// left of its table; a chip parked on the far right reads as a result, not a
// control, and gets missed.
const COLUMNS = [
  { key: 'status', label: 'Status', w: 80 },
  { key: 'sessionId', label: 'Session ID', w: 300 },
  { key: 'anchor', label: 'User?', w: 120 },
  { key: 'ipList', label: 'IP Addresses', w: 330 },
  { key: 'firstSeen', label: 'First Seen', w: 170 },
  { key: 'lastSeen', label: 'Last Seen', w: 170 },
  { key: 'osList', label: 'OS', w: 120 },
  { key: 'clientList', label: 'Browser / Client', w: 170 },
  { key: 'eventCount', label: 'Events', w: 80 },
];

/**
 * Best available client label for one event.
 * Sign-ins carry a UserAgent; Exchange carries ClientInfoString (surfaced as
 * ClientInfo) which names the actual mail client. Neither is present on every
 * workload, so an event that has neither contributes nothing rather than
 * polluting the column with "Unknown".
 */
function clientLabel(evt) {
  if (evt.UserAgent) {
    const b = parseBrowser(evt.UserAgent);
    if (b && b !== 'Unknown') return b;
  }
  if (evt.ClientInfo) {
    // ClientInfoString is often "Client=OWA;Action=ViaProxy" or a long UA.
    const m = String(evt.ClientInfo).match(/Client=([^;]+)/i);
    if (m) return m[1].trim();
    return String(evt.ClientInfo).slice(0, 40);
  }
  if (evt.ClientAppUsed) return String(evt.ClientAppUsed);
  return null;
}

/**
 * Every IP an event puts on the board: its own, an inferred one, and any
 * candidates a service-proxied event resolved to.
 */
function eventIps(evt) {
  const out = [];
  const ip = evt.IPAddress || evt.InferredIP;
  if (ip) out.push(String(ip));
  if (evt.AllResolvedIPs) {
    for (const r of String(evt.AllResolvedIPs).split(',')) {
      const t = r.trim();
      if (t) out.push(t);
    }
  }
  return out;
}

function osLabel(evt) {
  const os = evt.NormalizedOS || evt.DeviceOS;
  if (!os) return null;
  const s = String(os).trim();
  if (!s || s.toLowerCase() === 'unknown') return null;
  return s;
}

function SortIcon({ active, dir }) {
  if (!active) return <span className="text-gray-700 ml-1">↕</span>;
  return dir === 'asc'
    ? <ChevronUpIcon className="h-3 w-3 inline ml-1" />
    : <ChevronDownIcon className="h-3 w-3 inline ml-1" />;
}

export default function SessionsTab({
  results,
  userBaseline = null,
  ipData = [],
  ipClassifications = {},
  tenantSafeIPs = [],
  compromisedSessions = new Map(),
  sessionClassifications = {},
  onCycleSessionStatus,
  selectedTz,
}) {
  const [sortKey, setSortKey] = useState('firstSeen');
  const [sortDir, setSortDir] = useState('desc');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Shared drag-to-resize, same hook AlertsPage / ActivityLog / the Coverage
  // pages use — rather than a fourth private implementation.
  const { colWidths, thRefs, tableRef, startResize } = useResizableCols(COLUMNS.map((c) => c.w));

  const tenantSafeSet = useMemo(() => new Set(tenantSafeIPs), [tenantSafeIPs]);

  // The user's OWN historical sign-in IPs, from the 7-day behaviour baseline.
  // This is the strongest available "was this really them?" signal, because it
  // is derived from the user's history rather than from anything an analyst or
  // an attacker can influence during the incident.
  const baselineIpSet = useMemo(() => {
    const raw = userBaseline?.knownIPs;
    const list = Array.isArray(raw) ? raw : (raw ? String(raw).split(',') : []);
    return new Set(list.map((s) => String(s).trim()).filter(Boolean));
  }, [userBaseline]);

  // ip -> enrichment, from the same rows the IP Addresses tab renders. The
  // provider is often the fastest read in the whole table: "AT&T / Residential"
  // beside "Tier.Net / Hosting" under one session says token replay before any
  // classification has been applied.
  const ipMeta = useMemo(() => {
    const m = new Map();
    for (const row of ipData || []) {
      if (row?.IPAddress) m.set(String(row.IPAddress), row);
    }
    return m;
  }, [ipData]);

  // EXACTLY the rule IPAddressTable.getIPStatus uses: explicit classification >
  // tenant safe default > default. The same IP must not carry two different
  // verdicts on two tabs of the same case.
  //
  // Do NOT substitute CaseDetail's `effectiveSafeIPSet` here. That set also
  // absorbs service IPs and every IP a *known enterprise app* was seen from —
  // an inference about the app, not about the address. In a stolen-token case
  // the attacker drives the same app as the victim (One Outlook Web / OWA), so
  // an unclassified attacker IP would inherit "safe", render green, and — if it
  // were the only status in the session — silence the mixed-classification flag
  // that is the entire point of this tab.
  const ipStatusOf = useCallback((ip) => {
    const explicit = ipClassifications[ip];
    if (explicit) return explicit;
    if (tenantSafeSet.has(ip)) return 'safe';
    return 'default';
  }, [ipClassifications, tenantSafeSet]);

  const { sessions, ipSpread } = useMemo(() => {
    if (!results) return { sessions: [], ipSpread: new Map() };
    const map = new Map();
    // ip -> { sessions:Set<sid>, sessionless:number }. Drives the "only seen in
    // this session" mark. Session-less events MUST be counted: ~36% of events in
    // a real case carry no session id, so an IP can look session-unique here
    // while appearing all over the case — the mark would be a lie.
    const spread = new Map();
    const noteIp = (ip, sid) => {
      let rec = spread.get(ip);
      if (!rec) { rec = { sessions: new Set(), sessionless: 0 }; spread.set(ip, rec); }
      if (sid) rec.sessions.add(sid); else rec.sessionless++;
    };

    for (const [listKey, workloadLabel] of SESSION_SOURCES) {
      const events = results[listKey];
      if (!Array.isArray(events)) continue;

      for (const evt of events) {
        const sid = evt.SessionId || evt.AADSessionId;
        if (!sid) {
          for (const ip of new Set(eventIps(evt))) noteIp(ip, null);
          continue;
        }
        for (const ip of new Set(eventIps(evt))) noteIp(ip, sid);

        let s = map.get(sid);
        if (!s) {
          s = {
            sessionId: sid,
            ips: new Set(),
            osSet: new Set(),
            clientSet: new Set(),
            workloads: new Set(),
            firstSeen: null,
            lastSeen: null,
            eventCount: 0,
          };
          map.set(sid, s);
        }

        // An event's own IP, plus any real IPs a service-proxied event resolved
        // to — otherwise Exchange rows contribute only Microsoft addresses and
        // the session looks like it never touched a user IP.
        for (const ip of eventIps(evt)) s.ips.add(ip);

        const os = osLabel(evt);
        if (os) s.osSet.add(os);
        const cl = clientLabel(evt);
        if (cl) s.clientSet.add(cl);
        s.workloads.add(workloadLabel);

        const ts = evt.EventTimeUTC;
        if (ts) {
          if (!s.firstSeen || ts < s.firstSeen) s.firstSeen = ts;
          if (!s.lastSeen || ts > s.lastSeen) s.lastSeen = ts;
        }
        s.eventCount++;
      }
    }

    const built = [...map.values()].map((s) => {
      const ips = [...s.ips].sort();
      const compromisedAt = compromisedSessions.get(s.sessionId) || null;
      const classification = sessionStatusOf(sessionClassifications, s.sessionId);
      // Compromised wins over an analyst "safe" mark — same precedence the
      // filters use, so the badge can't disagree with the filtering. A session the
      // analyst marked as the attacker's (v1.190.0) is compromised from its first row.
      const status = compromisedAt ? 'compromised' : (classification === 'safe' ? 'safe' : 'default');
      const attackerMark = classification === 'attacker' ? sessionClassifications[s.sessionId] : null;
      const ipStatuses = ips.map(ipStatusOf);
      const exclusiveIPs = new Set(
        ips.filter((ip) => {
          const rec = spread.get(ip);
          return rec && rec.sessions.size === 1 && rec.sessionless === 0;
        })
      );
      // Does this session contain an IP the user demonstrably signed in from
      // BEFORE the incident? On the reference case this is the one field that
      // separates an attacker's file access from an identical-looking legitimate
      // one — both arrive on a Microsoft backend IP with no client IP recorded,
      // and only the session's IP membership tells them apart.
      const baselineAnchored = baselineIpSet.size > 0 && ips.some((ip) => baselineIpSet.has(ip));
      return {
        ...s,
        ips,
        exclusiveIPs,
        exclusiveCount: exclusiveIPs.size,
        baselineAnchored,
        // 'baseline' sorts before 'none', so sorting the column groups the
        // unexplained sessions together at one end.
        anchor: baselineAnchored ? 'baseline' : 'none',
        anchorLabel: baselineAnchored ? 'baseline ip' : 'no baseline ip',
        ipList: ips.join(', '),
        osList: [...s.osSet].sort().join(', '),
        clientList: [...s.clientSet].sort().join(', '),
        workloadList: [...s.workloads].sort().join(', '),
        compromisedAt,
        attackerMark,
        status,
        // A session spanning >1 IP is the thing worth looking at; flag it when
        // the IPs disagree on classification (e.g. a safe IP and a malicious
        // one under one token).
        mixedIPs: ips.length > 1 && new Set(ipStatuses).size > 1,
      };
    });
    return { sessions: built, ipSpread: spread };
  }, [results, compromisedSessions, sessionClassifications, ipStatusOf, baselineIpSet]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = sessions;
    if (q) {
      rows = rows.filter((s) =>
        s.sessionId.toLowerCase().includes(q) ||
        s.ipList.toLowerCase().includes(q) ||
        s.osList.toLowerCase().includes(q) ||
        s.clientList.toLowerCase().includes(q) ||
        s.workloadList.toLowerCase().includes(q) ||
        s.anchorLabel.includes(q) ||
        // Provider/network-type are on screen, so they must be searchable —
        // "hosting" should pull up every session touching a datacenter.
        s.ips.some((ip) => {
          const m = ipMeta.get(ip);
          if (!m) return false;
          return `${m.Provider || m.ISP || ''} ${m.NetworkType || ''}`.toLowerCase().includes(q);
        })
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [sessions, query, sortKey, sortDir, ipMeta]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'firstSeen' || key === 'lastSeen' || key === 'eventCount' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const compromisedCount = sessions.filter((s) => s.status === 'compromised').length;
  const mixedCount = sessions.filter((s) => s.mixedIPs).length;
  const noBaselineCount = baselineIpSet.size > 0
    ? sessions.filter((s) => !s.baselineAnchored).length
    : 0;

  if (sessions.length === 0) {
    return (
      <div className="card text-center py-12">
        <KeyIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">No Session IDs Found</h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          None of the events in this investigation carry a sign-in <span className="font-mono">SessionId</span> or
          an Exchange/SharePoint <span className="font-mono">AADSessionId</span>. Legacy-auth and
          service-to-service events frequently have neither.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="card bg-gray-800/50 border-gray-700">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-300">
            <p>
              <span className="font-semibold text-gray-100">{sessions.length}</span> session
              {sessions.length === 1 ? '' : 's'} across the investigation.
              {compromisedCount > 0 && (
                <> <span className="font-semibold text-red-400">{compromisedCount}</span> touched a malicious IP.</>
              )}
              {mixedCount > 0 && (
                <> <span className="font-semibold text-amber-400">{mixedCount}</span> span IPs with differing classifications.</>
              )}
              {noBaselineCount > 0 && (
                <> <span className="font-semibold text-amber-400">{noBaselineCount}</span> contain no IP the user signed in from before the incident.</>
              )}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Sign-in <span className="font-mono">SessionId</span> and Exchange/SharePoint{' '}
              <span className="font-mono">AADSessionId</span> are treated as one namespace. A single session
              holding both a known-good and an attacker IP is the signature of token replay.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              <sup className="text-sky-400 font-bold">*</sup> marks an IP seen <span className="text-gray-300">only in
              this session</span> and nowhere else in the case — including events that carry no session id.
              <span className="text-gray-600"> ×N</span> means the IP is shared across N sessions. Hover any IP for the
              full breakdown.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              <span className="text-emerald-400">User?</span> answers whether the session contains an IP the user signed
              in from <span className="text-gray-300">before</span> the incident (their 7-day baseline). It is derived
              from the user's own history, so neither an analyst nor an attacker can influence it.
              <span className="text-amber-400"> no baseline IP</span> is not proof of compromise — a new office, a phone
              or a VPN all look the same — but an attacker-only session can never be anything else.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Click the circle in the <span className="text-gray-300">Status</span> column to mark a session
              <span className="text-green-300"> safe</span> — the same control the IP Addresses and URL Clicks tables
              use. Events under safe sessions are hidden by the "Safe sessions hidden" toolbar toggle on the Timeline.
              It is the same setting as the Attacker Window panel's "Other Sessions Seen" list, so a session marked here
              is marked there. A <span className="text-red-300">red</span> circle is compromised — derived, not
              clickable. Unlike that list, this table shows <span className="text-gray-300">every</span> session,
              including compromised ones and beyond its first 50.
            </p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          placeholder="Search session, IP, OS, client, workload..."
          className="input-field py-1.5 text-sm w-80"
        />
        <span className="text-xs text-gray-500">
          {filtered.length === sessions.length
            ? `${sessions.length} sessions`
            : `${filtered.length} of ${sessions.length} sessions`}
        </span>
      </div>

      {/* Matrix */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800 text-gray-500 text-xs">
                {COLUMNS.map((c, i) => (
                  <th
                    key={c.key}
                    ref={(el) => { thRefs.current[i] = el; }}
                    style={{ width: `${colWidths[i]}px` }}
                    onClick={() => toggleSort(c.key)}
                    className="text-left px-3 py-2 font-medium cursor-pointer hover:text-gray-300 whitespace-nowrap select-none relative"
                  >
                    {c.label}
                    <SortIcon active={sortKey === c.key} dir={sortDir} />
                    {/* Resize handle — stopPropagation so dragging doesn't sort */}
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => { e.stopPropagation(); startResize(e, i); }}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pageRows.map((s) => (
                <tr
                  key={s.sessionId}
                  className={`hover:bg-gray-800/30 ${s.status === 'compromised' ? 'bg-red-950/20' : ''}`}
                >
                  {/* Status — FIRST column, matching IPAddressTable: every
                      classification control in this portal lives on the left of
                      its table. Same chip and same handleCycleSessionStatus as the
                      Attacker Window panel's "Other Sessions Seen" list, so a
                      session marked safe here is marked safe there.

                      Compromised sessions render a non-clickable red chip, which
                      matches the panel (it excludes them from its markable list
                      entirely). "Compromised wins" is enforced at filter time, so
                      showing a green "safe" chip on one would contradict what the
                      filters actually do. */}
                  <td className="px-3 py-2 align-top">
                    <StatusCircle
                      tone={sessionStatusTone(s.status)}
                      /* Compromised is derived, not the analyst's mark, and it
                         overrides a safe mark at filter time — so it renders as an
                         inert circle rather than a button that appears to toggle
                         something it can't. */
                      onClick={s.status === 'compromised' ? undefined
                        : (onCycleSessionStatus ? () => onCycleSessionStatus(s.sessionId) : undefined)}
                      title={
                        s.attackerMark
                          ? `Marked as the attacker's session${s.attackerMark.by ? ` by ${s.attackerMark.by}` : ''}: “${s.attackerMark.reason}”. Compromised from its first row, ${s.compromisedAt}. Change it from the session id in an expanded timeline row.`
                          : s.status === 'compromised'
                          ? `Compromised — touched a malicious IP, first at ${s.compromisedAt}. This overrides a safe mark and can't be cleared here; un-classify the malicious IP instead.`
                          : s.status === 'safe'
                            ? 'Safe — click to clear'
                            : 'Default — click to mark this session Safe'
                      }
                    />
                  </td>

                  {/* Session ID */}
                  <td className="px-3 py-2 align-top overflow-hidden">
                    <div className="flex items-center gap-1 group">
                      <span
                        className={`font-mono text-xs truncate ${s.status === 'compromised' ? 'text-red-300' : 'text-gray-300'}`}
                        title={s.sessionId}
                      >
                        {s.sessionId}
                      </span>
                      <CopyButton text={s.sessionId} />
                    </div>
                    <div className="text-[11px] text-gray-600 mt-0.5 truncate" title={s.workloadList}>{s.workloadList}</div>
                  </td>

                  {/* User? — the session's own answer to "was this really them?",
                      independent of anything the analyst has classified. */}
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    {baselineIpSet.size === 0 ? (
                      <span className="text-xs text-gray-700" title="No 7-day sign-in baseline available for this user">—</span>
                    ) : s.baselineAnchored ? (
                      <span
                        className="text-xs text-emerald-400"
                        title="This session contains an IP the user signed in from BEFORE the incident (7-day baseline)"
                      >
                        ✓ baseline IP
                      </span>
                    ) : (
                      <span
                        className="text-xs text-amber-400"
                        title="No baseline IP in this session — the user has no pre-incident sign-in history from any address here. Not proof of compromise (new location, phone, VPN), but this is where an attacker-only session shows up."
                      >
                        no baseline IP
                      </span>
                    )}
                  </td>

                  {/* IPs — colored by the analyst's own classification */}
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-col gap-0.5">
                      {s.ips.length === 0 && <span className="text-gray-600">—</span>}
                      {s.ips.map((ip) => {
                        const rec = ipSpread.get(ip);
                        const nSessions = rec ? rec.sessions.size : 0;
                        const nLoose = rec ? rec.sessionless : 0;
                        const exclusive = s.exclusiveIPs.has(ip);
                        const title = exclusive
                          ? 'Seen ONLY in this session — nowhere else in the case'
                          : `Seen in ${nSessions} session${nSessions === 1 ? '' : 's'}` +
                            (nLoose > 0 ? `, plus ${nLoose} event${nLoose === 1 ? '' : 's'} with no session id` : '');
                        const meta = ipMeta.get(ip);
                        const provider = meta?.Provider || meta?.ISP || '';
                        const netType = meta?.NetworkType || '';
                        const geo = [meta?.City, meta?.State, meta?.Country].filter(Boolean).join(', ');
                        return (
                          <div key={ip} className="leading-tight" title={geo ? `${title}\n${geo}` : title}>
                            <span className="font-mono text-xs">
                              <span className={getIPStatusTextClass(ipStatusOf(ip))}>{ip}</span>
                              {exclusive && (
                                <sup className="text-sky-400 ml-0.5 font-sans font-bold" aria-label="only in this session">*</sup>
                              )}
                              {!exclusive && nSessions > 1 && (
                                <span className="text-gray-600 ml-1 font-sans">×{nSessions}</span>
                              )}
                            </span>
                            {/* Deliberately uncoloured. Colour in the IP column is
                                reserved for the analyst's own classification —
                                tinting network type competes with that vocabulary
                                and implies a verdict the portal has not made. */}
                            {/* Truncates to the (resizable) column width rather
                                than a hardcoded max — the analyst can widen it. */}
                            {(provider || netType) && (
                              <div className="text-[11px] text-gray-500 truncate">
                                {provider || '—'}
                                {netType && <span> · {netType}</span>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {s.mixedIPs && (
                      <div className="flex items-center gap-1 text-[11px] text-amber-400 mt-1">
                        <ShieldExclamationIcon className="h-3 w-3" />
                        mixed classification
                      </div>
                    )}
                  </td>

                  <td className="px-3 py-2 align-top text-xs text-gray-400 whitespace-nowrap">
                    {s.firstSeen ? formatTimestampShort(s.firstSeen, selectedTz) : '—'}
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-gray-400 whitespace-nowrap">
                    {s.lastSeen ? formatTimestampShort(s.lastSeen, selectedTz) : '—'}
                  </td>

                  <td className="px-3 py-2 align-top text-xs text-gray-300 overflow-hidden">
                    <span className="block truncate" title={s.osList}>{s.osList || <span className="text-gray-600">—</span>}</span>
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-gray-300 overflow-hidden">
                    <span className="block truncate" title={s.clientList}>{s.clientList || <span className="text-gray-600">—</span>}</span>
                  </td>

                  <td className="px-3 py-2 align-top text-xs text-gray-400">{s.eventCount}</td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length > pageSize && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
